import * as z from "zod";
import { TimeZoneSchema } from "./domain.js";
import { Id, IsoDate, RunStatus } from "./ids.js";
import { ExecutionWaitReasonSchema } from "./runs.js";

export const TIMELINE_MAX_SPACES = 32;
export const TIMELINE_MAX_BOTS = 200;
export const TIMELINE_MAX_ROUTINES = 500;
export const TIMELINE_MAX_ENTRIES = 2000;
export const TIMELINE_LABEL_CODEPOINTS = 120;
export const TIMELINE_SNIPPET_CODEPOINTS = 320;
export const TIMELINE_AVATAR_MAX_BYTES = 16_384;
export const TIMELINE_AVATAR_BUDGET_BYTES = 512 * 1024;
const TimelineId = Id.max(128).regex(/^[A-Za-z0-9_-]+$/);
const Label = z
  .string()
  .max(240)
  .refine((value) => [...value].length <= TIMELINE_LABEL_CODEPOINTS);
const Snippet = z
  .string()
  .max(640)
  .refine((value) => [...value].length <= TIMELINE_SNIPPET_CODEPOINTS);

export const RoutineTimelineInput = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timezone: TimeZoneSchema,
  botId: TimelineId.optional(),
  spaceId: TimelineId.optional(),
  includePaused: z.boolean().default(true),
});
export type RoutineTimelineQuery = z.infer<typeof RoutineTimelineInput>;

export const RoutineTimelineBotSchema = z.object({
  id: TimelineId,
  name: Label,
  color: z.string().max(TIMELINE_AVATAR_MAX_BYTES),
  avatarOmitted: z.boolean(),
  spaceId: TimelineId,
  spaceName: Label,
});
export type RoutineTimelineBot = z.infer<typeof RoutineTimelineBotSchema>;

export const RoutineTimelineRoutineSchema = z.object({
  id: TimelineId,
  promptSnippet: Snippet,
  active: z.boolean().optional(),
});
export type RoutineTimelineRoutine = z.infer<typeof RoutineTimelineRoutineSchema>;

export const RoutineTimelineProjectionRoutineSchema = RoutineTimelineRoutineSchema.extend({
  id: TimelineId,
  botId: TimelineId,
  spaceId: TimelineId,
  name: Label,
  crons: z.array(z.string()),
  timezone: z.string(),
  active: z.boolean(),
  updatedAt: IsoDate,
  createdAt: IsoDate,
  nextRunAt: IsoDate.nullable(),
  lastRunAt: IsoDate.nullable(),
  lastSkippedAt: IsoDate.nullable(),
  lastSkipReason: z.string().nullable(),
  scheduledOnceAt: IsoDate.nullable(),
});
export type RoutineTimelineProjectionRoutine = z.infer<
  typeof RoutineTimelineProjectionRoutineSchema
>;

export const RoutineTimelineEntrySchema = z.object({
  key: z.string().max(256),
  routineId: TimelineId.nullable(),
  originId: TimelineId.nullable(),
  botId: TimelineId,
  spaceId: TimelineId,
  name: Label,
  at: IsoDate,
  scheduledAt: IsoDate.nullable(),
  skipReason: z.string().max(64).nullable(),
  status: z.union([RunStatus, z.enum(["planned", "paused", "skipped"])]),
  runId: TimelineId.nullable(),
  startedAt: IsoDate.nullable(),
  completedAt: IsoDate.nullable(),
  executionWaitReason: ExecutionWaitReasonSchema.nullable(),
});
export type RoutineTimelineEntry = z.infer<typeof RoutineTimelineEntrySchema>;

export const RoutineTimelineSchema = z.object({
  day: z.string(),
  timezone: z.string(),
  from: IsoDate,
  to: IsoDate,
  now: IsoDate,
  bots: z.array(RoutineTimelineBotSchema).max(TIMELINE_MAX_BOTS),
  routines: z.array(RoutineTimelineRoutineSchema).max(TIMELINE_MAX_ROUTINES),
  entries: z.array(RoutineTimelineEntrySchema).max(TIMELINE_MAX_ENTRIES),
  partial: z.boolean(),
});
export type RoutineTimeline = z.infer<typeof RoutineTimelineSchema>;

export const RoutineTimelineEntryDetailsSchema = z.object({
  name: z.string().nullable(),
  prompt: z.string().nullable(),
});
export type RoutineTimelineEntryDetails = z.infer<typeof RoutineTimelineEntryDetailsSchema>;
